import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "./AuthContext";
import { fetchProfile, saveProfile as apiSaveProfile, deleteProfile as apiDeleteProfile } from "../lib/api";

const ProfileContext = createContext(null);

/**
 * Loads the member's profile once (or null if they haven't created one yet) so calculators can
 * pre-fill inputs from it and the Goal Calculator can offer a saved-goal picker (Task 10).
 * Depends on AuthProvider being an ancestor — only fetches once a member is logged in.
 */
export function ProfileProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load (or clear) the profile whenever auth state changes. The `active` flag drops a stale
  // in-flight response — e.g. a /profile request started while logged in that resolves *after* a
  // logout — so it can't repopulate a profile we just cleared.
  useEffect(() => {
    let active = true;
    if (!isAuthenticated) {
      setProfile(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetchProfile()
      .then((data) => { if (active) setProfile(data); })
      .catch(() => { if (active) setProfile(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isAuthenticated]);

  const save = useCallback(async (payload) => {
    const data = await apiSaveProfile(payload);
    setProfile(data);
    return data;
  }, []);

  const remove = useCallback(async () => {
    await apiDeleteProfile();
    setProfile(null);
  }, []);

  const value = useMemo(() => ({ profile, loading, save, remove }), [profile, loading, save, remove]);

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used within a ProfileProvider");
  return ctx;
}
